import { SendButton } from '@/components/ui/SendButton';
import { useIsPhone } from '@/mobile/layout';
/**
 * Inline thread previews (backlog #33): under each message with replies, show the newest few replies, a reply count,
 * participant avatars, "Show N more replies" (paged by per-conversation sequence), a quick-reply box and
 * "Open in sidebar". Collapse state is stored per user per channel on the server.
 */
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, CornerDownRight, PanelRightOpen } from 'lucide-react';
import type { Message, ThreadViewPrefs } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useMembers } from '@/lib/hooks';
import { messageStore, useConversationState } from '@/lib/messageStore';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, Spinner } from '@/components/ui';
import { MessageItem, PendingItem, type AckContext } from './MessageItem';
import { MessageTime } from '@/components/time/RelativeTime';
import { showThreadSummary } from './threadHeader';
import { runPositions, type BubbleRun } from './bubbles';
import { enterToSend } from '@/lib/enterToSend';

export const PREVIEW_COUNT = 3;

interface Prefs { collapsed: (id: string) => boolean; toggle: (id: string) => void; allCollapsed: boolean; setAll: (v: boolean) => void; canReply: boolean; /** t522u: false = adding reactions to these comments is turned off. */ canReact: boolean }
const PrefsCtx = createContext<Prefs | null>(null);

export function ThreadPrefsProvider({ conversationId, canReply, canReact = true, children }: { conversationId: string; canReply: boolean; canReact?: boolean; children: ReactNode }) {
  const toast = useUi(s => s.toast);
  const key = useMemo(() => ['thread-prefs', conversationId], [conversationId]);
  const q = useQuery({ queryKey: key, queryFn: () => api.threadPrefs(conversationId), staleTime: 60_000, retry: false });
  const prefs: ThreadViewPrefs = q.data ?? { all_collapsed: false, toggled_ids: [] };
  const save = async (b: Parameters<typeof api.setThreadPrefs>[1], optimistic: ThreadViewPrefs) => {
    const prev = prefs;
    queryClient.setQueryData(key, optimistic);
    try { queryClient.setQueryData(key, await api.setThreadPrefs(conversationId, b)); } catch (e) { queryClient.setQueryData(key, prev); toast(errorMessage(e), 'error'); }
  };
  const value: Prefs = {
    canReply, canReact,
    allCollapsed: prefs.all_collapsed,
    collapsed: id => prefs.all_collapsed !== prefs.toggled_ids.includes(id),
    toggle: id => {
      const on = !prefs.toggled_ids.includes(id);
      void save({ toggle: { message_id: id, on } }, { ...prefs, toggled_ids: on ? [...prefs.toggled_ids, id] : prefs.toggled_ids.filter(x => x !== id) });
    },
    setAll: v => void save({ all_collapsed: v }, { all_collapsed: v, toggled_ids: [] }),
  };
  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}
export const useThreadPrefs = () => useContext(PrefsCtx);

export function InlineThread({ root, ack, bubbles }: { root: Message; ack?: AckContext;
  /** t278u: replies as bubbles; the value says whether names and avatars show. Undefined: the comment layout. */
  bubbles?: boolean }) {
  const prefs = useThreadPrefs();
  const state = useConversationState(root.conversation_id);
  const { byId } = useMembers();
  const openThread = useUi(s => s.openThread);
  const toast = useUi(s => s.toast);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const phone = useIsPhone();
  const [draft, setDraft] = useState('');
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const loaded = state.threads[root.id] ?? [];
  const more = state.threadMore[root.id];
  const people = state.threadPeople[root.id] ?? [];
  const pending = state.pending.filter(p => p.parent_id === root.id);
  const collapsed = prefs?.collapsed(root.id) ?? false;
  const shown = expanded ? loaded : loaded.slice(-PREVIEW_COUNT);
  const runs = useMemo(() => {
    if (bubbles === undefined) return null;
    const out = new Map<string, BubbleRun>();
    const all = state.threads[root.id] ?? [];
    for (const [id, r] of runPositions(expanded ? all : all.slice(-PREVIEW_COUNT))) out.set(id, { ...r, names: bubbles });
    return out;
  }, [state.threads, root.id, expanded, bubbles]);
  const hidden = Math.max(0, root.reply_count - shown.length);
  const regionId = `thread-${root.id}`;
  const label = `${root.reply_count} ${root.reply_count === 1 ? 'reply' : 'replies'}`;
  // t267u: open with every reply on screen, the count and last-reply time only repeat what is visible.
  const summary = showThreadSummary({ collapsed, shownCount: shown.length, replyCount: root.reply_count });

  const showMore = async () => {
    if (!expanded && loaded.length > shown.length) { setExpanded(true); if (more == null) return; }
    setExpanded(true);
    if (more == null) return;
    setLoading(true);
    try { await messageStore.loadOlderReplies(root.conversation_id, root.id, 20); } catch (e) { toast(errorMessage(e), 'error'); } finally { setLoading(false); }
  };
  const send = () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    void messageStore.send(root.conversation_id, { body, parent_id: root.id, mentions: [], attachment_ids: [] });
  };

  return (
    <div className="mt-1.5" data-testid="inline-thread">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {summary ? (
        <button onClick={() => prefs?.toggle(root.id)} aria-expanded={!collapsed} aria-controls={regionId} data-testid="thread-summary"
          className="flex items-center gap-1.5 rounded-md px-1 py-0.5 font-semibold text-accent-fg hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-black dark:hover:bg-slate-800"
          title={collapsed ? 'Show replies here' : 'Hide replies'}>
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          <span className="flex -space-x-1.5" aria-hidden>
            {people.slice(0, 5).map(u => <span key={u} className="rounded-full ring-2 ring-white dark:ring-slate-950"><Avatar size={18} name={byId.get(u)?.display_name} id={u} /></span>)}
          </span>
          <span>{label}</span>
          {root.last_reply_at && <span className="font-normal text-slate-500">Last reply <MessageTime iso={root.last_reply_at} inline testId="thread-last-reply" /></span>}
          <span className="sr-only">{people.length ? `from ${people.map(u => byId.get(u)?.display_name ?? 'someone').join(', ')}` : ''}</span>
        </button>
        ) : (
        <button onClick={() => prefs?.toggle(root.id)} aria-expanded aria-controls={regionId} aria-label={`Hide ${label}`} title="Hide replies" data-testid="thread-collapse"
          className="flex h-6 w-6 items-center justify-center rounded-md text-accent-fg hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-black dark:hover:bg-slate-800">
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        )}
        <button onClick={() => openThread(root.id)} className="flex items-center gap-1 rounded-md px-1 py-0.5 text-slate-500 hover:bg-slate-100 hover:text-black focus-visible:ring-2 focus-visible:ring-black dark:hover:bg-slate-800 dark:hover:text-white">
          <PanelRightOpen className="h-3.5 w-3.5" />Open in sidebar
        </button>
      </div>
      {!collapsed && (
        <div id={regionId} role="group" aria-label={`Replies to ${byId.get(root.sender_id)?.display_name ?? 'message'}`}
          className="ml-1 mt-1 border-l-2 border-black pl-2 dark:border-slate-500">
          {hidden > 0 && (
            <button onClick={() => void showMore()} disabled={loading} data-testid="show-more-replies"
              className="mb-1 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-black hover:bg-brand-yellow focus-visible:ring-2 focus-visible:ring-black dark:text-slate-200 hover:text-on-accent-strong dark:hover:text-on-accent-strong">
              {loading ? <Spinner className="h-3 w-3" /> : <CornerDownRight className="h-3.5 w-3.5" />}
              Show {hidden} more {hidden === 1 ? 'reply' : 'replies'}
            </button>
          )}
          {expanded && loaded.length > PREVIEW_COUNT && (
            <button onClick={() => setExpanded(false)} className="mb-1 rounded-md px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-black dark:hover:bg-slate-800">Show fewer</button>
          )}
          <div className="-mx-3">
            {shown.map((m, i, arr) => (
              <MessageItem key={m.id} m={m} inThread ack={ack} bubble={runs?.get(m.id)}
                compact={i > 0 && arr[i - 1].sender_id === m.sender_id && new Date(m.created_at).getTime() - new Date(arr[i - 1].created_at).getTime() < 5 * 60_000} />
            ))}
            {pending.map(p => <PendingItem key={p.client_message_id} p={p} conversationId={root.conversation_id} bubble={bubbles !== undefined} />)}
          </div>
          {prefs?.canReply !== false && (
            <form className="mt-1 flex items-end gap-1.5" onSubmit={e => { e.preventDefault(); send(); }}>
              <label htmlFor={`qr-${root.id}`} className="sr-only">Reply in thread</label>
              <textarea id={`qr-${root.id}`} ref={replyRef} rows={1} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Reply…" data-testid="quick-reply"
                onKeyDown={enterToSend({ value: draft, onSend: send, onCancel: () => { setDraft(''); replyRef.current?.blur(); } })}
                className="min-h-8 flex-1 resize-none rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black dark:border-slate-700 dark:bg-slate-900" />
              <EmojiButton onPick={e => { const r = insertAtCaret(replyRef.current, draft, e); setDraft(r.value); restoreCaret(replyRef.current, r.caret); }} />
              <SendButton phone={phone} type="submit" label="Send reply" disabled={!draft.trim()} />
            </form>
          )}
        </div>
      )}
    </div>
  );
}

/** Header control: collapse or expand every inline thread in this channel (saved per user). */
export function ThreadsToggle({ conversationId }: { conversationId: string }) {
  const toast = useUi(s => s.toast);
  const key = ['thread-prefs', conversationId];
  const q = useQuery({ queryKey: key, queryFn: () => api.threadPrefs(conversationId), staleTime: 60_000, retry: false });
  const all = q.data?.all_collapsed ?? false;
  const flip = async () => {
    const prev = q.data;
    queryClient.setQueryData(key, { all_collapsed: !all, toggled_ids: [] });
    try { queryClient.setQueryData(key, await api.setThreadPrefs(conversationId, { all_collapsed: !all })); } catch (e) { queryClient.setQueryData(key, prev); toast(errorMessage(e), 'error'); }
  };
  return (
    <button onClick={() => void flip()} aria-pressed={!all} title={all ? 'Show thread replies inline' : 'Collapse all inline threads'} data-testid="threads-toggle"
      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-black dark:text-slate-300 dark:hover:bg-slate-800">
      {all ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}{all ? 'Threads collapsed' : 'Threads inline'}
    </button>
  );
}
